import { createColumnHelper } from '@tanstack/react-table';

import Copyable from '../../components/Copyable';
import { EmptyValue } from '../../components/EmptyValue';
import { clsxm } from '../../utils';
import TableColumnHeader from '../TableColumnHeader';

import type { CellContext } from '@tanstack/react-table';
import type { ColumnFactory, CopyableCellProps, CopyableColumnOptions } from './types';

/**
 * Factory function to create a copyable column with consistent styling
 *
 * @example
 * ```tsx
 * const ID_COLUMN = createCopyableColumn<Customer>({
 *   accessor: 'id',
 *   label: 'Customer ID',
 *   maxWidth: '100px'
 * });
 * ```
 */
export function createCopyableColumn<TData>(config: {
  accessor: keyof TData & string;
  label: string;
  options?: CopyableColumnOptions<TData>;
  /**
   * Copyable component used to render the cell. Defaults to @bridge/ui's Copyable.
   * Apps can inject a wrapped variant (e.g. the dashboard's toast-enabled Copyable).
   */
  CopyableComponent?: React.ComponentType<CopyableCellProps>;
}): ColumnFactory<TData, string> {
  const { accessor, label, options = {}, CopyableComponent = Copyable } = config;
  const { size = 150, className, formatter, truncate = false, truncateOptions, meta, maxWidth } = options;

  const columnHelper = createColumnHelper<TData>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return columnHelper.accessor(accessor as any, {
    size,
    meta,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    header: ({ table }: { table: any }) => (
      <TableColumnHeader accessor={accessor} label={label} tableState={table.getState()} />
    ),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: CellContext<TData, any>) => {
      const value = info.getValue() as string;

      // Handle null/undefined values
      if (value == null) {
        return <EmptyValue className={className} size="md" />;
      }

      const displayValue = formatter ? formatter(value, info.row.original) : value;

      return (
        <CopyableComponent
          className={clsxm(className, { truncate: truncate })}
          style={maxWidth ? { maxWidth: `${maxWidth}px` } : undefined}
          truncateOptions={truncateOptions}
        >
          {displayValue}
        </CopyableComponent>
      );
    },
  });
}
